/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useEffect } from "react";

import { Lesson } from "types";
import like from "assets/like.png";
import ClassCard from "./ClassCard";
import { ClassCardContainer, CardFooterButton } from "./ClassCardStyle";

type ClassListProps = {
  lessons: Lesson[];
  section?: string;
  allowsBooking?: boolean;
  hidesDate?: boolean;
  hidesFooter?: boolean;
  dynamicFooter?: boolean;
  allowsStacking?: boolean;
  showRibbon?: {
    completed: boolean;
    rated: boolean;
    past: boolean;
  };
  nextCourseId?: number;
  setCourseFromLocationId?: (id: number) => void;
};

// ClassStack will be a collection of ClassCards that share the
// same date and time. It will only show one card at a time.
function ClassStack({
  lessons,
  section,
  allowsBooking = false,
  hidesDate = false,
  hidesFooter = false,
  dynamicFooter = false,
  showRibbon = {
    completed: false,
    rated: false,
    past: false,
  },
}: Omit<ClassListProps, "allowsStacking">) {
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(lessons[0]);

  useEffect(() => {
    setCurrentLesson(lessons[currentLessonIndex]);
  }, [currentLessonIndex, lessons]);

  const onNextCardInStack = () => {
    const nextIndex =
      currentLessonIndex === lessons.length - 1 ? 0 : currentLessonIndex + 1;
    setCurrentLessonIndex(nextIndex);
  };

  const onPrevCardInStack = () => {
    const nextIndex =
      currentLessonIndex === 0 ? lessons.length - 1 : currentLessonIndex - 1;
    setCurrentLessonIndex(nextIndex);
  };

  return (
    <ClassCard
      key={currentLesson.id}
      lesson={currentLesson}
      section={section}
      onNextCardInStack={onNextCardInStack}
      onPrevCardInStack={onPrevCardInStack}
      isStacked={lessons.length > 1}
      allowsBooking={allowsBooking}
      hidesDate={hidesDate}
      // if it's the filter result of Book A Class page and
      // the class is 1 on 1, then show no footer. This is an edge case
      // just for filter-result.
      hidesFooter={
        section === "filter-result" && currentLesson.classType === "1 on 1"
          ? false
          : hidesFooter
      }
      dynamicFooter={dynamicFooter}
      showRibbon={showRibbon}
    />
  );
}

/**
 * Usage: Used to display an array of classes of any size. Must be wrapped by an antd Row
 * component with gutter={24}. See example in MyClasses/ClassDetails/BookAClass etc.
 *
 * @props lessons: the classes/lessons to be displayed. Can be null.
 *
 * @props section: the section in which the cards are to be displayed. If possible,
 * please refactor such that section is no longer needed. Right now, it's only useful
 * for an edge case in the filter result where 1 on 1 classes cards require a specific layout.
 * Not a required prop.
 *
 * @props allowsBooking: allows cards to show the booking modal when user clicks on them.
 * Not a required prop. Default value is false.
 *
 * @props hidesDate: hides the date in the card header. Not a required prop, Default is false.
 *
 * @props hidesFooter: hides the card's footer. Not a required prop. Default is false.
 *
 * @props dynamicFooter: allows the footer text to change depending on the class' state, such
 * as ADD TO CALENDAR when class has not started yet, MY CLASS NOTES when class is finished,
 * and so on. When this prop is true, footer will query for appointment data to display the
 * appropriate text. Not a required prop. Default value is false
 *
 * @props allowsStacking: allows cards of the same day and time to be stacked together.
 * Not a required prop. Default value is false.
 */
function ClassList({
  lessons,
  section,
  allowsBooking = false,
  hidesDate = false,
  hidesFooter = false,
  dynamicFooter = false,
  allowsStacking = false,
  showRibbon = {
    completed: false,
    rated: false,
    past: false,
  },
  nextCourseId,
  setCourseFromLocationId,
}: ClassListProps) {
  // If stacking is allowed, the stacking logic is performed.
  // ClassStack will be used instead of normal ClassCards.
  const navigateToCourse = () => {
    if (nextCourseId && setCourseFromLocationId) {
      setCourseFromLocationId(nextCourseId);
    }
  };
  if (allowsStacking && lessons) {
    // STACKING LOGIC
    const listOfClassStacks: Lesson[][] = [];
    let classStack: Lesson[] = [];
    let classTime: string = lessons[0]?.time;
    for (let i = 0; i < lessons.length; i += 1) {
      const lesson = lessons[i];
      // group classes of the same time into a stack
      if (lesson.time === classTime) {
        classStack.push(lesson);
      } else {
        // if current class is not at the same time as previous classes,
        // then update classTime to reflect the new classStack time
        // and reset current stack
        classTime = lesson.time;
        listOfClassStacks.push(classStack);
        const newStack: Lesson[] = [lesson];
        classStack = newStack;
      }
    }
    // add final classStack to list
    listOfClassStacks.push(classStack);

    const stackList = listOfClassStacks.map((stack: Lesson[]) => {
      stack.sort(
        (firstLesson: Lesson, secondLesson: Lesson) =>
          firstLesson.seatsRemaining - secondLesson.seatsRemaining
      );
      return (
        <ClassStack
          key={stack[0].id}
          lessons={stack}
          section={section}
          allowsBooking={allowsBooking}
          hidesDate={hidesDate}
          hidesFooter={hidesFooter}
          dynamicFooter={dynamicFooter}
          showRibbon={showRibbon}
        />
      );
    });
    return <>{stackList}</>;
  }

  // Else, if stacking is not allowed, return a regular
  // array of class cards.
  const classList = lessons?.map((lesson: Lesson) => (
    <ClassCard
      key={lesson.id}
      lesson={lesson}
      section={section}
      // Since cards are not stacked, provide dummy functions
      onNextCardInStack={() => {
        // do nothing
      }}
      onPrevCardInStack={() => {
        // do nothing
      }}
      isStacked={false}
      allowsBooking={allowsBooking}
      hidesDate={hidesDate}
      hidesFooter={hidesFooter}
      dynamicFooter={dynamicFooter}
      showRibbon={showRibbon}
    />
  ));
  return (
    <>
      {classList}
      {nextCourseId && (
        <ClassCardContainer xs={24} sm={12} md={8} lg={6} xl={6}>
          <button className="follow" type="button" onClick={navigateToCourse}>
            <div className="follow-header">YOU ARE DOING GREAT!!!</div>
            <div className="follow-content">
              <div className="follow-text">
                Keep going and join the next level follow-on course
              </div>
              <img className="follow-img" src={like} alt="Like" />
            </div>
            <CardFooterButton className="follow-button">
              JOIN FOLLOW ON COURSE
            </CardFooterButton>
          </button>
        </ClassCardContainer>
      )}
    </>
  );
}

export default ClassList;
